import { marked } from "marked";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  ExternalHyperlink,
  LevelFormat,
  AlignmentType,
  type INumberingOptions,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType,
  BorderStyle,
  ShadingType,
  convertInchesToTwip,
  TableLayoutType,
} from "docx";

/** Page size options for Word documents */
export type PageSizeOption = "A4" | "Letter" | "Legal";

/** Page dimensions in twips (twentieths of a point) */
const PAGE_SIZES: Record<PageSizeOption, { width: number; height: number }> = {
  A4: { width: 11906, height: 16838 }, // 210mm × 297mm
  Letter: { width: 12240, height: 15840 }, // 8.5in × 11in
  Legal: { width: 12240, height: 20160 }, // 8.5in × 14in
};

interface DocxOptions {
  title?: string;
  /**
   * Author stamped into the document metadata. Callers own this value and derive it
   * from the active brand, so no vendor or tenant name is hardcoded here. A white-label
   * deployment must never stamp another brand's name into an exported file.
   */
  creator?: string;
  description?: string;
  /** Font family to use for body text (e.g., "Poppins", "Calibri"). Defaults to system default. */
  fontFamily?: string;
  /** Font family to use for code blocks (e.g., "Fira Code", "Courier New"). Defaults to "Courier New". */
  codeFontFamily?: string;
  /** Page size for the document. Defaults to A4. */
  pageSize?: PageSizeOption;
}

/** Default font for code blocks */
const DEFAULT_CODE_FONT = "Courier New";

/** Font context passed through conversion functions */
interface FontContext {
  fontFamily?: string;
  codeFontFamily: string;
}

/**
 * High-level function to convert markdown to DOCX Blob
 * This is the main export that matches the old API
 */
export async function convertMarkdownToDocx(
  markdown: string,
  options: DocxOptions = {},
): Promise<Blob> {
  const fontContext: FontContext = {
    fontFamily: options.fontFamily,
    codeFontFamily: options.codeFontFamily || DEFAULT_CODE_FONT,
  };

  const { paragraphs, numbering } = await convertMarkdownToDocxParagraphs(markdown, fontContext);

  // Build default styles for the document with custom font
  const styles = options.fontFamily
    ? {
        default: {
          document: {
            run: {
              font: options.fontFamily,
            },
          },
        },
      }
    : undefined;

  // Get page size dimensions (default to A4)
  const pageSizeConfig = PAGE_SIZES[options.pageSize || "A4"];

  const doc = new Document({
    creator: options.creator || "",
    title: options.title || "Document",
    description: options.description || "",
    numbering: numbering,
    styles: styles,
    sections: [
      {
        properties: {
          page: {
            size: {
              width: pageSizeConfig.width,
              height: pageSizeConfig.height,
            },
          },
        },
        children: paragraphs,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);

  return blob;
}

/**
 * Converts Markdown text to docx Paragraph and Table elements using marked library
 * Follows same pattern as pdfRenderer.ts but outputs Word format
 *
 * Returns both paragraphs/tables and optional numbering configuration for proper Word list styles
 *
 * @param markdown - The markdown text to convert
 * @param fontContext - Optional font configuration for custom fonts
 */
export async function convertMarkdownToDocxParagraphs(
  markdown: string,
  fontContext?: FontContext,
): Promise<{ paragraphs: (Paragraph | DocxTable)[]; numbering?: INumberingOptions }> {
  // Use default font context if not provided
  const fonts: FontContext = fontContext || {
    fontFamily: undefined,
    codeFontFamily: DEFAULT_CODE_FONT,
  };

  // Convert markdown to HTML using marked
  const html = await marked(markdown, {
    gfm: true,
    breaks: false,
  });

  // Parse HTML into DOM
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const paragraphs: (Paragraph | DocxTable)[] = [];
  let hasOrderedList = false;

  // Process each child node in the body
  for (const node of Array.from(doc.body.childNodes)) {
    const converted = convertHtmlNodeToDocx(node as HTMLElement, 0, fonts);

    if (converted) {
      paragraphs.push(...converted);

      // Track if we have ordered lists
      if (node.nodeName === "OL") {
        hasOrderedList = true;
      }
    }
  }

  // Generate numbering configuration if document contains ordered lists
  const numbering = hasOrderedList
    ? {
        config: [
          {
            reference: "ordered-list",
            levels: [
              {
                level: 0,
                format: LevelFormat.DECIMAL,
                text: "%1.",
                alignment: AlignmentType.START,
                start: 1,
                style: {
                  paragraph: {
                    indent: { left: 720, hanging: 260 },
                  },
                },
              },
              {
                level: 1,
                format: LevelFormat.DECIMAL,
                text: "%2.",
                alignment: AlignmentType.START,
                start: 1,
                style: {
                  paragraph: {
                    indent: { left: 1440, hanging: 260 },
                  },
                },
              },
              {
                level: 2,
                format: LevelFormat.DECIMAL,
                text: "%3.",
                alignment: AlignmentType.START,
                start: 1,
                style: {
                  paragraph: {
                    indent: { left: 2160, hanging: 260 },
                  },
                },
              },
              {
                level: 3,
                format: LevelFormat.DECIMAL,
                text: "%4.",
                alignment: AlignmentType.START,
                start: 1,
                style: {
                  paragraph: {
                    indent: { left: 2880, hanging: 260 },
                  },
                },
              },
              {
                level: 4,
                format: LevelFormat.DECIMAL,
                text: "%5.",
                alignment: AlignmentType.START,
                start: 1,
                style: {
                  paragraph: {
                    indent: { left: 3600, hanging: 260 },
                  },
                },
              },
              {
                level: 5,
                format: LevelFormat.DECIMAL,
                text: "%6.",
                alignment: AlignmentType.START,
                start: 1,
                style: {
                  paragraph: {
                    indent: { left: 4320, hanging: 260 },
                  },
                },
              },
            ],
          },
        ],
      }
    : undefined;

  return { paragraphs, numbering };
}

/**
 * Convert HTML node to docx elements
 */
function convertHtmlNodeToDocx(
  node: HTMLElement,
  level: number = 0,
  fonts: FontContext,
): (Paragraph | DocxTable)[] {
  if (!node || !node.nodeName) return [];

  const nodeName = node.nodeName.toLowerCase();

  switch (nodeName) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
      return [convertHeading(node, nodeName, fonts)];

    case "p":
      return [convertParagraph(node, fonts)];

    case "ul":
      return convertList(node, false, level, fonts);

    case "ol":
      return convertList(node, true, level, fonts);

    case "pre":
      return [convertCodeBlock(node, fonts)];

    case "table":
      return [convertTable(node, fonts)];

    case "blockquote":
      return convertBlockquote(node, fonts);

    case "hr":
      return [
        new Paragraph({
          text: "",
          spacing: { before: 200, after: 200 },
        }),
      ];

    default:
      return [];
  }
}

/**
 * Convert heading element to docx Paragraph
 */
function convertHeading(node: HTMLElement, tagName: string, fonts: FontContext): Paragraph {
  const textRuns = convertInlineElements(node, fonts);

  const headingLevels: Record<string, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
    h1: HeadingLevel.HEADING_1,
    h2: HeadingLevel.HEADING_2,
    h3: HeadingLevel.HEADING_3,
    h4: HeadingLevel.HEADING_4,
    h5: HeadingLevel.HEADING_5,
    h6: HeadingLevel.HEADING_6,
  };

  return new Paragraph({
    children: textRuns,
    heading: headingLevels[tagName] || HeadingLevel.HEADING_1,
    spacing: { before: 200, after: 100 },
  });
}

/**
 * Convert paragraph element to docx Paragraph
 */
function convertParagraph(node: HTMLElement, fonts: FontContext): Paragraph {
  const textRuns = convertInlineElements(node, fonts);

  return new Paragraph({
    children: textRuns,
    spacing: { before: 100, after: 100 },
  });
}

/**
 * Convert list (ul/ol) to docx Paragraphs
 */
function convertList(
  node: HTMLElement,
  ordered: boolean,
  level: number,
  fonts: FontContext,
): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  for (const child of Array.from(node.children)) {
    if (child.nodeName === "LI") {
      paragraphs.push(...convertListItem(child as HTMLElement, ordered, level, fonts));
    }
  }

  return paragraphs;
}

/**
 * Convert list item to docx Paragraph(s)
 */
function convertListItem(
  node: HTMLElement,
  ordered: boolean,
  level: number,
  fonts: FontContext,
): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  // Check for task list (checkbox)
  const firstChild = node.firstChild;
  let isTaskList = false;
  let isChecked = false;

  if (
    firstChild &&
    firstChild.nodeType === window.Node.ELEMENT_NODE &&
    (firstChild as HTMLElement).tagName === "INPUT"
  ) {
    const input = firstChild as HTMLInputElement;

    if (input.type === "checkbox") {
      isTaskList = true;
      isChecked = input.checked;
    }
  }

  // Separate nested lists from text content
  const nestedLists: HTMLElement[] = [];
  const contentNodes: globalThis.Node[] = [];

  for (const child of Array.from(node.childNodes)) {
    // Skip checkbox inputs
    if (child.nodeType === window.Node.ELEMENT_NODE && (child as HTMLElement).tagName === "INPUT") {
      continue;
    }

    // Collect nested lists separately
    if (child.nodeType === window.Node.ELEMENT_NODE) {
      const element = child as HTMLElement;

      if (element.tagName === "UL" || element.tagName === "OL") {
        nestedLists.push(element);
        continue;
      }
    }

    // Collect all other content (text and inline elements)
    contentNodes.push(child);
  }

  // Create paragraph for the list item text content
  if (contentNodes.length > 0) {
    const textRuns: (TextRun | ExternalHyperlink)[] = [];

    for (const child of contentNodes) {
      textRuns.push(...convertInlineContent(child, {}, fonts));
    }

    if (textRuns.length > 0) {
      // Add checkbox symbol for task lists
      if (isTaskList) {
        const checkboxSymbol = isChecked ? "☑" : "☐";

        textRuns.unshift(
          new TextRun({
            text: checkboxSymbol + " ",
            font: fonts.fontFamily,
            size: 22,
          }),
        );
      }

      if (ordered) {
        // Ordered list with proper Word numbering
        paragraphs.push(
          new Paragraph({
            children: textRuns,
            numbering: {
              reference: "ordered-list",
              level: level,
            },
            spacing: { before: 100, after: 100 },
          }),
        );
      } else {
        // Unordered list with bullet (or task list without bullet)
        paragraphs.push(
          new Paragraph({
            children: textRuns,
            bullet: isTaskList ? undefined : { level: level },
            spacing: { before: 100, after: 100 },
            indent: isTaskList ? { left: 720 * (level + 1) } : undefined,
          }),
        );
      }
    }
  }

  // Process nested lists after the main list item
  for (const nestedList of nestedLists) {
    const isNestedOrdered = nestedList.tagName === "OL";

    paragraphs.push(...convertList(nestedList, isNestedOrdered, level + 1, fonts));
  }

  return paragraphs;
}

/**
 * Convert code block (pre/code) to docx Paragraph
 */
function convertCodeBlock(node: HTMLElement, fonts: FontContext): Paragraph {
  const codeElement = node.querySelector("code") || node;
  const text = codeElement.textContent || "";
  const lines = text.split("\n");

  const textRuns = lines.map(
    (line, index) =>
      new TextRun({
        text: index === lines.length - 1 ? line : line + "\n",
        font: fonts.codeFontFamily,
        size: 20,
      }),
  );

  return new Paragraph({
    children: textRuns,
    spacing: { before: 100, after: 100 },
    shading: {
      type: ShadingType.SOLID,
      color: "F5F5F5",
    },
  });
}

/**
 * Convert inline HTML elements to TextRuns and Hyperlinks
 */
function convertInlineElements(
  node: HTMLElement,
  fonts: FontContext,
): (TextRun | ExternalHyperlink)[] {
  const result: (TextRun | ExternalHyperlink)[] = [];

  for (const child of Array.from(node.childNodes)) {
    result.push(...convertInlineContent(child, {}, fonts));
  }

  return result;
}

/**
 * Convert inline content (text nodes and inline elements) to TextRuns
 */
function convertInlineContent(
  node: globalThis.Node,
  inherited: { bold?: boolean; italic?: boolean; strike?: boolean } = {},
  fonts: FontContext,
): (TextRun | ExternalHyperlink)[] {
  if (node.nodeType === window.Node.TEXT_NODE) {
    const text = node.textContent || "";

    if (text.trim() === "") return [];

    return [
      new TextRun({
        text: text,
        font: fonts.fontFamily,
        size: 22,
        bold: inherited.bold,
        italics: inherited.italic,
        strike: inherited.strike,
      }),
    ];
  }

  if (node.nodeType === window.Node.ELEMENT_NODE) {
    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    switch (tagName) {
      case "strong":
      case "b":
        return processInlineChildren(element, { ...inherited, bold: true }, fonts);

      case "em":
      case "i":
        return processInlineChildren(element, { ...inherited, italic: true }, fonts);

      case "del":
      case "s":
        return processInlineChildren(element, { ...inherited, strike: true }, fonts);

      case "code":
        return [
          new TextRun({
            text: element.textContent || "",
            font: fonts.codeFontFamily,
            size: 20,
            bold: inherited.bold,
            italics: inherited.italic,
            strike: inherited.strike,
          }),
        ];

      case "a": {
        const href = element.getAttribute("href") || "";
        const linkText = element.textContent || "";

        // Skip empty links
        if (!linkText.trim() || !href) {
          return [];
        }

        // Create hyperlink with explicit blue color and underline
        // This ensures Word recognizes and displays it as a clickable link
        return [
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: linkText,
                font: fonts.fontFamily,
                size: 22,
                style: "Hyperlink", // Use Word's built-in Hyperlink style
                color: "0000FF", // Explicit blue color
                underline: {
                  type: "single",
                },
                bold: inherited.bold,
                italics: inherited.italic,
                strike: inherited.strike,
              }),
            ],
            link: href,
          }),
        ];
      }

      case "br":
        return [new TextRun({ text: "\n", font: fonts.fontFamily, size: 22 })];

      default:
        return processInlineChildren(element, inherited, fonts);
    }
  }

  return [];
}

/**
 * Process children of inline element
 */
function processInlineChildren(
  element: HTMLElement,
  inherited: { bold?: boolean; italic?: boolean; strike?: boolean },
  fonts: FontContext,
): (TextRun | ExternalHyperlink)[] {
  const result: (TextRun | ExternalHyperlink)[] = [];

  for (const child of Array.from(element.childNodes)) {
    result.push(...convertInlineContent(child, inherited, fonts));
  }

  return result;
}

/**
 * Convert table to docx Table
 */
function convertTable(node: HTMLElement, fonts: FontContext): DocxTable {
  const rows: DocxTableRow[] = [];
  let numColumns = 0;

  // First pass: count columns
  for (const section of Array.from(node.children)) {
    if (section.tagName === "THEAD" || section.tagName === "TBODY") {
      for (const row of Array.from(section.children)) {
        if (row.tagName === "TR") {
          const cells = Array.from(row.children).filter(
            (c) => c.tagName === "TD" || c.tagName === "TH",
          );

          numColumns = Math.max(numColumns, cells.length);
        }
      }
    }
  }

  // Calculate column width in DXA (twips) - A4 page width is ~9360 twips usable
  // Combined with TableLayoutType.FIXED and columnWidths array for proper spacing
  const pageWidthTwips = 9360;
  const columnWidthTwips =
    numColumns > 0 ? Math.floor(pageWidthTwips / numColumns) : pageWidthTwips;

  // Second pass: process rows
  for (const section of Array.from(node.children)) {
    if (section.tagName === "THEAD" || section.tagName === "TBODY") {
      for (const row of Array.from(section.children)) {
        if (row.tagName === "TR") {
          rows.push(
            convertTableRow(
              row as HTMLElement,
              section.tagName === "THEAD",
              columnWidthTwips,
              fonts,
            ),
          );
        }
      }
    }
  }

  // Create columnWidths array - each column gets equal width in twips
  const columnWidths = Array(numColumns).fill(columnWidthTwips);

  return new DocxTable({
    rows: rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.FIXED,
    columnWidths: columnWidths,
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
    },
  });
}

/**
 * Convert table row to docx TableRow
 */
function convertTableRow(
  row: HTMLElement,
  isHeader: boolean,
  columnWidthTwips: number,
  fonts: FontContext,
): DocxTableRow {
  const cells: DocxTableCell[] = [];

  for (const cell of Array.from(row.children)) {
    if (cell.tagName === "TD" || cell.tagName === "TH") {
      cells.push(convertTableCell(cell as HTMLElement, isHeader, columnWidthTwips, fonts));
    }
  }

  return new DocxTableRow({ children: cells });
}

/**
 * Convert table cell to docx TableCell
 */
function convertTableCell(
  cell: HTMLElement,
  isHeader: boolean,
  columnWidthTwips: number,
  fonts: FontContext,
): DocxTableCell {
  const text = cell.textContent || "";
  const align = cell.getAttribute("align") || "left";

  const alignment =
    align === "center"
      ? AlignmentType.CENTER
      : align === "right"
        ? AlignmentType.RIGHT
        : AlignmentType.LEFT;

  return new DocxTableCell({
    children: [
      new Paragraph({
        children: [new TextRun({ text: text, font: fonts.fontFamily, size: 22, bold: isHeader })],
        alignment: alignment,
      }),
    ],
    // Use absolute width in twips (DXA) for better compatibility with Pages and Word
    width: { size: columnWidthTwips, type: WidthType.DXA },
    shading: isHeader ? { type: ShadingType.SOLID, color: "F0F0F0" } : undefined,
    margins: {
      top: 100,
      bottom: 100,
      left: 120,
      right: 120,
    },
  });
}

/**
 * Convert blockquote to docx Paragraphs
 */
function convertBlockquote(node: HTMLElement, fonts: FontContext): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  for (const child of Array.from(node.children)) {
    if (child.tagName === "P") {
      const textRuns = convertInlineElements(child as HTMLElement, fonts);

      paragraphs.push(
        new Paragraph({
          children: textRuns,
          indent: { left: convertInchesToTwip(0.5) },
          spacing: { before: 100, after: 100 },
          shading: { type: ShadingType.SOLID, color: "F5F5F5" },
          border: {
            left: { style: BorderStyle.SINGLE, size: 12, color: "999999" },
          },
        }),
      );
    }
  }

  return paragraphs;
}
